import { timelineTranscriptSnippet, type TimelineUtterance } from "@/lib/timeline-transcript-snippet";

export function quantizePlayhead(atSec: number, stepSec = 0.4): number {
  if (!Number.isFinite(atSec) || atSec < 0) return 0;
  const steps = Math.round(atSec / stepSec);
  return Math.round(steps * stepSec * 1000) / 1000;
}

/** Last item whose time is <= atSec (stable between sparse events). */
export function activeTimedIndex<T>(items: T[], atSec: number, atOf: (item: T) => number): number {
  if (!items.length) return -1;
  let idx = -1;
  for (let i = 0; i < items.length; i++) {
    const t = atOf(items[i]);
    if (!Number.isFinite(t)) continue;
    if (t <= atSec + 0.08) idx = i;
    else break;
  }
  return idx;
}

export type VideoModalContextLayer = {
  id: "language" | "voice" | "camera" | "acoustic";
  title: string;
  body: string;
  atSec?: number;
};

export type VideoModalContext = {
  atSec: number;
  transcript: {
    agent?: string;
    user?: string;
    /** Who is speaking at the playhead (highlights user line when both exist). */
    activeSpeaker: "agent" | "user" | "other";
  };
  layers: VideoModalContextLayer[];
};

type Moment = { at: number; label: string };

type VoiceRow = {
  atMs?: number | null;
  valence?: number;
  arousal?: number;
  energy?: number;
  speechRate?: number;
  model?: string;
};

type AcousticRow = { t_ms?: number; label?: string; score?: number };

function formatPct(n: number | undefined): string | null {
  if (n == null || !Number.isFinite(n)) return null;
  const v = n <= 1 ? Math.round(n * 100) : Math.round(n);
  return `${v}%`;
}

function voiceSummary(row: VoiceRow): string {
  const parts: string[] = [];
  const val = formatPct(row.valence);
  const ar = formatPct(row.arousal);
  if (val) parts.push(`valence ${val}`);
  if (ar) parts.push(`arousal ${ar}`);
  if (row.energy != null && Number.isFinite(row.energy)) {
    parts.push(`energy ${row.energy.toFixed(2)}`);
  }
  if (row.speechRate != null && Number.isFinite(row.speechRate)) {
    parts.push(`speech rate ${row.speechRate.toFixed(2)}`);
  }
  const model = String(row.model || "");
  if (model.startsWith("text_map:")) {
    parts.push(`from language (${model.slice("text_map:".length)})`);
  } else if (model && model !== "none") {
    parts.push("voice model");
  }
  return parts.length ? parts.join(" · ") : "No voice metrics on this turn.";
}

function acousticsAt(rows: AcousticRow[], atSec: number, windowSec = 2.8): { label: string; score?: number } | null {
  const atMs = atSec * 1000;
  let best: { label: string; score?: number; rank: number } | null = null;
  for (const r of rows) {
    const t = Number(r.t_ms);
    if (!Number.isFinite(t)) continue;
    const dist = Math.abs(t - atMs);
    if (dist > windowSec * 1000) continue;
    const score = Number(r.score);
    const rank = dist - (Number.isFinite(score) ? score * 180 : 0);
    const label = String(r.label || "").trim();
    if (!label) continue;
    if (!best || rank < best.rank) {
      best = { label, score: Number.isFinite(score) ? score : undefined, rank };
    }
  }
  return best;
}

function speakerKey(speaker: string) {
  const s = String(speaker || "").toLowerCase();
  if (s.includes("user")) return "user";
  if (s.includes("agent") || s.includes("assistant")) return "agent";
  return s;
}

export function buildVideoModalContext(input: {
  atSec: number;
  durationSec: number;
  transcriptRows: Array<{ at: number; speaker: string; text: string; agent: boolean; debug?: boolean }>;
  moments: Moment[];
  perception: Record<string, unknown> | null;
  videoSignals?: string | null;
  videoAppearance?: string | null;
  cameraLive: boolean;
}): VideoModalContext {
  const atSec = quantizePlayhead(input.atSec);
  const durationSec = Math.max(1, input.durationSec || 1);

  const utterances: TimelineUtterance[] = input.transcriptRows
    .filter((r) => !r.debug && String(r.text || "").trim())
    .map((r) => ({
      startMs: Math.round(r.at * 1000),
      speaker: r.speaker,
      text: r.text,
    }));
  const snippet = timelineTranscriptSnippet(utterances, atSec, durationSec);

  let activeSpeaker: "agent" | "user" | "other" = "other";
  for (let i = utterances.length - 1; i >= 0; i--) {
    if (utterances[i].startMs / 1000 <= atSec + 0.05) {
      const key = speakerKey(utterances[i].speaker);
      activeSpeaker = key === "agent" || key === "user" ? key : "other";
      break;
    }
  }

  const layers: VideoModalContextLayer[] = [];
  const moments = [...input.moments].sort((a, b) => a.at - b.at);
  const mIdx = activeTimedIndex(moments, atSec, (m) => m.at);
  if (mIdx >= 0) {
    const m = moments[mIdx];
    layers.push({
      id: "language",
      title: "Language",
      body: `Mood marker: ${m.label}.`,
      atSec: m.at,
    });
  }

  const voiceRows = Array.isArray(input.perception?.voice)
    ? (input.perception?.voice as VoiceRow[])
    : [];
  const voiceTimed = voiceRows
    .map((v) => ({ row: v, at: Number(v.atMs) / 1000 }))
    .filter((x) => Number.isFinite(x.at))
    .sort((a, b) => a.at - b.at);
  const vIdx = activeTimedIndex(voiceTimed, atSec, (x) => x.at);
  if (vIdx >= 0) {
    layers.push({
      id: "voice",
      title: "Voice",
      body: voiceSummary(voiceTimed[vIdx].row),
      atSec: voiceTimed[vIdx].at,
    });
  }

  const acoustics = Array.isArray(input.perception?.acoustics)
    ? (input.perception?.acoustics as AcousticRow[])
    : [];
  const acoustic = acousticsAt(acoustics, atSec);
  if (acoustic) {
    layers.push({
      id: "acoustic",
      title: "Acoustic events",
      body: acoustic.score != null
        ? `${acoustic.label} (${Math.round(acoustic.score * 100)}% confidence)`
        : acoustic.label,
    });
  }

  const cameraBody = input.cameraLive
    ? (input.videoSignals || input.videoAppearance || "").trim().split(/(?<=[.!?])\s/)[0]
    : "";
  if (cameraBody) {
    layers.push({
      id: "camera",
      title: "Camera",
      body: cameraBody,
    });
  }

  return {
    atSec,
    transcript: {
      agent: snippet.agent,
      user: snippet.user,
      activeSpeaker,
    },
    layers,
  };
}
